import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';

// Safety-forward medical summary for the athlete profile: allergies, active
// injuries, and clearance restrictions surfaced where coaching decisions happen.
// Reads the existing scoped endpoints (medical-profile / injuries / clearances —
// all requireAthleteAccess: staff, parent→child, athlete→self) so it's robust to
// the medical table's schema variance. Renders nothing if access is denied.
const asList = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return [];
    try { const p = JSON.parse(s); return Array.isArray(p) ? p : [s]; }
    catch { return s.split(/[,;\n]/).map(x => x.trim()).filter(Boolean); }
  }
  return [];
};
const nameOf = (x) => (typeof x === 'string' ? x : (x?.name || x?.condition || x?.allergen || x?.type || '')) || '';

export default function MedicalSafetyCard({ athleteId, viewerRole }) {
  const [profile, setProfile] = useState(null);
  const [injuries, setInjuries] = useState([]);
  const [clearances, setClearances] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get(`${API_BASE_URL}/api/athletes/${athleteId}/medical-profile`),
      api.get(`${API_BASE_URL}/api/athletes/${athleteId}/injuries`),
      api.get(`${API_BASE_URL}/api/athletes/${athleteId}/clearances`),
    ]).then(([p, i, c]) => {
      if (!alive) return;
      if (p.status === 'rejected' && p.reason?.response?.status === 403) setDenied(true);
      setProfile(p.status === 'fulfilled' ? p.value.data : null);
      setInjuries(i.status === 'fulfilled' && Array.isArray(i.value.data) ? i.value.data : []);
      setClearances(c.status === 'fulfilled' && Array.isArray(c.value.data) ? c.value.data : []);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [athleteId]);

  if (!loaded || denied) return null;

  // The medical-profile endpoint returns { profile: {...}, medications: [...] };
  // fall back to a flat shape defensively.
  const mp = profile?.profile ?? profile ?? {};
  const meds = Array.isArray(profile?.medications) ? profile.medications
    : (Array.isArray(mp.medications) ? mp.medications : []);
  const allergies = asList(mp.allergies).map(nameOf).filter(Boolean);
  const conditions = asList(mp.chronic_conditions ?? mp.medical_conditions).map(nameOf).filter(Boolean);
  const medications = meds.map(m => m.medication_name || m.name || nameOf(m)).filter(Boolean);
  const activeInjuries = injuries.filter(x => {
    const s = String(x.injury_status || x.status || '').toLowerCase();
    return s === 'active' || s === 'recovering' || s === 'chronic' || (!s && !x.return_date && !x.recovery_date);
  });
  const restrictions = clearances.filter(x => {
    const s = String(x.clearance_status || x.status || '').toLowerCase();
    return s && !['cleared', 'full', 'active', 'approved', 'complete'].includes(s);
  });

  const isStaff = ['admin', 'coach', 'superadmin', 'staff'].includes(viewerRole);
  const hasAlert = allergies.length || activeInjuries.length || restrictions.length;
  const hasInfo = hasAlert || conditions.length || medications.length;
  const injuryLabel = (x) => [nameOf(x) || x.injury_type, x.body_part].filter(Boolean).join(' · ') || 'injury';
  const restrictionLabel = (x) => nameOf(x) || x.apparatus || x.event || x.restriction || 'restricted';

  const badge = (bg, color, text, key) => (
    <span key={key} style={{ background: bg, color, borderRadius: 999, padding: '2px 10px', fontSize: '.78rem', fontWeight: 700 }}>{text}</span>
  );

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '0.9rem 1.1rem', marginBottom: 14,
      border: `1px solid ${hasAlert ? '#fecaca' : '#e5e7eb'}`,
      borderLeft: `4px solid ${hasAlert ? '#dc2626' : '#cbd5e1'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: hasInfo ? 8 : 0 }}>
        <h2 style={{ margin: 0, fontSize: '1rem' }}>🏥 Medical</h2>
        {!hasInfo && <span style={{ color: '#15803d', fontSize: '.82rem', fontWeight: 600 }}>✅ Nothing on file</span>}
        {isStaff && <Link to="/medical-profile" style={{ marginLeft: 'auto', fontSize: '.8rem', color: '#4f46e5' }}>Manage →</Link>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {allergies.map((a, i) => badge('#fee2e2', '#991b1b', `⚠️ Allergy: ${a}`, `al${i}`))}
        {restrictions.map((r, i) => badge('#fee2e2', '#991b1b', `⛔ Not cleared: ${restrictionLabel(r)}`, `re${i}`))}
        {activeInjuries.map((x, i) => badge('#ffedd5', '#9a3412', `🩹 ${injuryLabel(x)}`, `in${i}`))}
        {medications.map((m, i) => badge('#eef2ff', '#3730a3', `💊 ${m}`, `me${i}`))}
        {conditions.map((c, i) => badge('#f1f5f9', '#475569', c, `co${i}`))}
      </div>
    </div>
  );
}
